/** Números do desenho: triângulos, chamadas e imagens por segundo.
 *
 *  Vêm do `three`, que já os conta por nós em `renderer.info`. Aqui só
 *  se lê e se calcula a taxa de imagens.
 *
 *  Duas decisões que valem a pena explicar:
 *
 *  - As imagens por segundo contam-se por amostragem e não a cada
 *    imagem. Contar a cada imagem obrigava a entrar no ciclo de
 *    desenho, e a barra de estado não tem nada que lá mexer. Medir de
 *    meio em meio segundo dá um número estável e honesto.
 *
 *  - A memória vem de `performance.memory`, que só existe no Chromium.
 *    Noutros browsers a coluna desaparece, em vez de mostrar um zero
 *    que ninguém sabe interpretar.
 */

export interface RendererStats {
  /** Triângulos desenhados na última imagem. */
  triangulos: number
  /** Chamadas de desenho na última imagem. */
  chamadas: number
  /** Imagens por segundo, ou null enquanto não houver amostra. */
  fps: number | null
  /** Megabytes de memória do JavaScript, ou null onde não se sabe. */
  memoriaMB: number | null
  /** 'WebGL2', 'WebGL' ou null. */
  motor: string | null
}

export const ESTATISTICAS_VAZIAS: RendererStats = {
  triangulos: 0,
  chamadas: 0,
  fps: null,
  memoriaMB: null,
  motor: null,
}

interface InfoDoThree {
  render?: { triangles?: number; calls?: number; frame?: number }
}

interface MemoriaDoBrowser {
  usedJSHeapSize?: number
}

/** Lê a memória do JavaScript, onde o browser a dá.
 *
 *  `performance.memory` não é norma e não existe no Firefox nem no
 *  Safari. Devolver null é o que faz a coluna desaparecer. */
export function lerMemoriaMB(
  perf: Performance | undefined = typeof performance === 'undefined'
    ? undefined
    : performance,
): number | null {
  const memoria = (perf as unknown as { memory?: MemoriaDoBrowser } | undefined)
    ?.memory
  const bytes = memoria?.usedJSHeapSize
  if (typeof bytes !== 'number' || !Number.isFinite(bytes)) return null
  return Math.round(bytes / (1024 * 1024))
}

/** Que versão do WebGL está a desenhar isto.
 *
 *  O `three` não o diz de forma directa, mas o contexto do canvas sim:
 *  um `WebGL2RenderingContext` é WebGL 2. */
export function lerMotor(contexto: unknown): string | null {
  if (!contexto) return null
  if (
    typeof WebGL2RenderingContext !== 'undefined' &&
    contexto instanceof WebGL2RenderingContext
  ) {
    return 'WebGL2'
  }
  if (
    typeof WebGLRenderingContext !== 'undefined' &&
    contexto instanceof WebGLRenderingContext
  ) {
    return 'WebGL'
  }
  return null
}

/** Imagens por segundo entre duas amostras.
 *
 *  O `three` conta as imagens em `info.render.frame`. A diferença
 *  entre dois valores, a dividir pelo tempo entre eles, dá a taxa.
 *  Devolve null quando não dá para saber: primeira amostra, relógio
 *  que não andou, ou contador que recuou (o `three` põe-no a zero
 *  quando o renderer é recriado, e nessa altura o certo é não dizer
 *  nada em vez de mostrar um número negativo). */
export function calcularFps(
  frameAnterior: number,
  frameAgora: number,
  msDecorridos: number,
): number | null {
  if (msDecorridos <= 0) return null
  const imagens = frameAgora - frameAnterior
  if (imagens < 0) return null
  return Math.round((imagens * 1000) / msDecorridos)
}

/** Junta tudo a partir do renderer do three. */
export function lerEstatisticas(
  info: InfoDoThree | null | undefined,
  contexto: unknown,
  fps: number | null,
): RendererStats {
  return {
    triangulos: info?.render?.triangles ?? 0,
    chamadas: info?.render?.calls ?? 0,
    fps,
    memoriaMB: lerMemoriaMB(),
    motor: lerMotor(contexto),
  }
}

/** Formata um número grande para a barra: 1234567 fica "1,2 M".
 *
 *  A barra é estreita e o número exacto de triângulos não interessa a
 *  ninguém; a ordem de grandeza interessa muito. */
export function formatarNumero(n: number): string {
  if (!Number.isFinite(n)) return '-'
  if (n < 1000) return String(n)
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)} k`
  return `${(n / 1_000_000).toFixed(1)} M`.replace('.', ',')
}
